var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Direct browser -> S3 multipart upload.
 *
 * Video bytes go from `MediaRecorder` to S3 without passing through Django. The
 * backend only issues presigned URLs and completes the upload; it never sees a
 * frame. That is the entire point of the redesign — the previous path pushed
 * base64-inflated video through the interview WebSocket and then had the server
 * download it all back to reassemble it.
 *
 * This is S3 Multipart Upload (a byte-range upload protocol), NOT
 * `multipart/form-data`, and NOT application-level video concatenation. S3
 * assembles the final object from parts by part number; we never join files.
 *
 * ON ETAGS. Each `PUT` returns an `ETag` header, and we collect them when we
 * can. We deliberately do not depend on them: reading a response header from a
 * cross-origin S3 request requires the bucket's CORS policy to expose `ETag`,
 * and that is an infrastructure setting outside this repo. The backend
 * reconciles with `ListParts` at completion time, which is authoritative
 * regardless. Anything collected here is a cross-check, not a source of truth.
 */
import { PartBuffer } from './part-buffer';
export const DEFAULT_UPLOADER_OPTIONS = {
    maxAttemptsPerPart: 5,
    baseBackoffMs: 500,
    concurrency: 3,
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
/** S3 signals an expired/invalid signature with 403. A fresh URL is the fix;
 *  retrying the same one never helps. */
const isExpiry = (status) => status === 403;
const isRetryable = (status) => status === 0 || status === 408 || status === 429 || status >= 500;
/**
 * Uploads parts, retrying each on its own part number.
 *
 * Retrying by part number is what makes recovery safe: re-PUTting part N simply
 * replaces it in S3, so a retry can never reorder or duplicate bytes.
 */
export class MultipartUploader {
    constructor(client, uploadId, options = {}) {
        this.client = client;
        this.uploadId = uploadId;
        this.urlCache = new Map();
        this.etags = new Map();
        this.active = 0;
        this.aborted = false;
        this.opts = Object.assign(Object.assign({}, DEFAULT_UPLOADER_OPTIONS), options);
    }
    get collectedEtags() {
        return [...this.etags.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([partNumber, etag]) => ({ partNumber, etag }));
    }
    urlFor(partNumber_1) {
        return __awaiter(this, arguments, void 0, function* (partNumber, forceRefresh = false) {
            if (!forceRefresh) {
                const cached = this.urlCache.get(partNumber);
                if (cached)
                    return cached.url;
            }
            const [issued] = yield this.client.partUrls(this.uploadId, [partNumber]);
            if (!issued)
                throw new Error(`backend issued no URL for part ${partNumber}`);
            this.urlCache.set(partNumber, issued);
            return issued.url;
        });
    }
    /** Upload one part, retrying it by number. Throws only once the budget is
     *  spent — the caller decides whether that is fatal. */
    upload(part) {
        return __awaiter(this, void 0, void 0, function* () {
            while (this.active >= this.opts.concurrency && !this.aborted) {
                yield sleep(50);
            }
            if (this.aborted)
                return;
            this.active++;
            try {
                let refresh = false;
                let lastErr = null;
                let fatal = null;
                for (let attempt = 1; attempt <= this.opts.maxAttemptsPerPart; attempt++) {
                    if (this.aborted)
                        return;
                    try {
                        const url = yield this.urlFor(part.partNumber, refresh);
                        const res = yield fetch(url, { method: 'PUT', body: part.blob });
                        if (res.ok) {
                            // May be null cross-origin unless the bucket exposes ETag. Not fatal.
                            const etag = res.headers.get('ETag');
                            if (etag)
                                this.etags.set(part.partNumber, etag.replaceAll('"', ''));
                            else
                                this.etags.set(part.partNumber, '');
                            this.urlCache.delete(part.partNumber);
                            return;
                        }
                        if (isExpiry(res.status)) {
                            // Re-sign and retry the SAME bytes — the buffer still holds them.
                            refresh = true;
                            lastErr = new Error(`part ${part.partNumber}: presigned URL expired (403)`);
                        }
                        else if (isRetryable(res.status)) {
                            lastErr = new Error(`part ${part.partNumber}: HTTP ${res.status}`);
                        }
                        else {
                            // Permanent: a malformed request or a rejected part will fail the
                            // same way every time. Burning the retry budget on it only delays
                            // the failure report by seconds and hammers S3 meanwhile.
                            //
                            // This must NOT be a bare `throw`: the surrounding catch below
                            // would swallow it back into `lastErr` and keep looping, which is
                            // exactly how the branch was previously defeated by its own
                            // error handling.
                            fatal = new Error(`part ${part.partNumber}: non-retryable HTTP ${res.status}`);
                        }
                    }
                    catch (err) {
                        lastErr = err;
                    }
                    if (fatal)
                        throw fatal;
                    yield sleep(this.opts.baseBackoffMs * Math.pow(2, (attempt - 1)));
                }
                throw lastErr !== null && lastErr !== void 0 ? lastErr : new Error(`part ${part.partNumber} failed`);
            }
            finally {
                this.active--;
            }
        });
    }
    waitForIdle() {
        return __awaiter(this, void 0, void 0, function* () {
            while (this.active > 0)
                yield sleep(50);
        });
    }
    abort() {
        this.aborted = true;
    }
}
export class S3MultipartSink {
    constructor(uploader, client, uploadId, events = {}, bufferOptions = {}, store) {
        this.uploader = uploader;
        this.client = client;
        this.uploadId = uploadId;
        this.events = events;
        this.store = store;
        this._bytes = 0;
        this._partsUploaded = 0;
        this.failedParts = 0;
        this.buffer = new PartBuffer({
            uploadPart: (part) => this.uploadPart(part),
            onPause: (n) => {
                var _a, _b, _c, _d;
                (_b = (_a = this.events).onDegraded) === null || _b === void 0 ? void 0 : _b.call(_a, `upload backlog hit the ceiling (${n} bytes buffered)`);
                (_d = (_c = this.events).onPause) === null || _d === void 0 ? void 0 : _d.call(_c, n);
            },
            onResume: (n) => { var _a, _b; return (_b = (_a = this.events).onResume) === null || _b === void 0 ? void 0 : _b.call(_a, n); },
            onSoftLimit: (n) => { var _a, _b; return (_b = (_a = this.events).onSoftLimit) === null || _b === void 0 ? void 0 : _b.call(_a, n); },
            // VA-28098. Both are fire-and-forget and both swallow: durability is
            // insurance on the recording, and insurance that can break the thing
            // it insures is worse than none. A store that is failing degrades to
            // the old behaviour — bytes held only in this page — and nothing else.
            persistChunk: (blob, partNumber) => {
                var _a;
                void ((_a = this.store) === null || _a === void 0 ? void 0 : _a.append(blob, partNumber).catch(() => undefined));
            },
            dropPersistedPart: (partNumber) => {
                var _a;
                void ((_a = this.store) === null || _a === void 0 ? void 0 : _a.dropPart(partNumber).catch(() => undefined));
            },
        }, bufferOptions);
    }
    get bytesAccepted() {
        return this._bytes;
    }
    get partsUploaded() {
        return this._partsUploaded;
    }
    get bufferedBytes() {
        return this.buffer.outstandingBytes;
    }
    /** Where the recorder's Blobs enter. Accumulated, never held in full. */
    accept(blob) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!blob || blob.size === 0)
                return;
            this._bytes += blob.size;
            yield this.buffer.add(blob);
        });
    }
    uploadPart(part) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            try {
                yield this.uploader.upload(part);
                this._partsUploaded++;
            }
            catch (err) {
                this.failedParts++;
                (_b = (_a = this.events).onFailure) === null || _b === void 0 ? void 0 : _b.call(_a, `part ${part.partNumber} exhausted its retries`, err);
                (_d = (_c = this.events).onDegraded) === null || _d === void 0 ? void 0 : _d.call(_c, `part ${part.partNumber} failed to upload`);
                throw err;
            }
        });
    }
    /**
     * Flush the tail and finalise.
     *
     * Completion is attempted even when a part failed: the backend reconciles
     * against S3 ListParts and will either complete a valid contiguous prefix or
     * refuse. Deciding that here, from the browser's partial view, would be
     * guessing — and after a crash there is no browser left to ask at all.
     */
    finalize(durationSec) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.buffer.flushFinal();
            yield this.uploader.waitForIdle();
            yield this.redriveHeldParts();
            yield this.client.complete(this.uploadId, this.uploader.collectedEtags, durationSec);
        });
    }
    /**
     * VA-28224 defect 1 — ask for the held bytes while there is still a page to
     * ask. This is the fix for a lost recording, and the mechanism is worth
     * stating because it is not the obvious one.
     *
     * A part that exhausts its retries is reported and rethrown, and
     * `PartBuffer` then DELIBERATELY keeps its bytes in IndexedDB — "a part that
     * never landed is precisely what a later page must be able to recover". That
     * is correct. The gap was that the only consumer of those bytes,
     * `recoverAbandonedChunks`, runs before `init` on the NEXT page load. An
     * interview that runs one continuous session never reloads, so the recovery
     * machinery sat on exactly the right bytes and was never asked for them.
     *
     * WHY THIS WORKS WHEN THE INLINE RETRIES DID NOT. Not a bigger budget — the
     * stall that killed the part was 171 s against 7.5 s of patience, and no
     * fixed budget covers an arbitrary outage. What changes is ELAPSED TIME: by
     * finalize that stall had been over for fourteen minutes. One more attempt at
     * a different moment beats twenty at the same one.
     *
     * THREE THINGS THIS MUST NOT DO, each of which silently breaks it:
     *
     * 1. Upload through a NEW `MultipartUploader`. `recoverAbandonedChunks` does,
     *    correctly, because it re-drives a DEAD page's upload id. Here the upload
     *    is ours and `complete()` is called with `this.uploader.collectedEtags` —
     *    so a part re-PUT through a fresh uploader lands in S3 and is still
     *    absent from the etag list. The completion stays exactly as incomplete as
     *    it was, and nothing errors.
     * 2. Clear the store. Those bytes are the only copy, and if `complete()`
     *    fails they are what a later page recovers from.
     * 3. Throw. A failed re-drive must fall through to `complete()` exactly as
     *    today; the worst case has to be unchanged behaviour, never a worse one.
     */
    redriveHeldParts() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const store = this.store;
            if (!store)
                return;
            let held = null;
            try {
                held = yield store.recover();
            }
            catch (_e) {
                return; // A store we cannot read is the situation we already shipped.
            }
            if (!held || held.parts.length === 0)
                return;
            // The chunks are tagged with the part numbers of whichever upload was live
            // when they were written. If that is not OUR upload, they belong to a dead
            // page that `recoverAbandonedChunks` never drained, and replaying them here
            // would assemble another recording's bytes into this one.
            if (held.ref.uploadId !== this.uploadId)
                return;
            // Anything S3 has already acknowledged is done. In a clean session every
            // part was dropped on acknowledgement, so this empties the list and the
            // whole method costs one IndexedDB read.
            const acknowledged = new Set(this.uploader.collectedEtags.map((e) => e.partNumber));
            const missing = held.parts.filter((p) => !acknowledged.has(p.partNumber));
            if (missing.length === 0)
                return;
            // Bounded in WALL time, not in attempts. Per-part budgets multiply: eight
            // stranded parts at 7.5 s each would hold a finished interview on screen
            // for a minute while the candidate waits to be told they can leave.
            const deadline = Date.now() + FINALIZE_REDRIVE_BUDGET_MS;
            let recovered = 0;
            for (const part of missing) {
                if (Date.now() > deadline)
                    break;
                try {
                    // Ascending part order, and through OUR uploader — see (1) above.
                    yield this.uploader.upload({
                        partNumber: part.partNumber,
                        blob: part.blob,
                        size: part.size,
                    });
                    recovered += 1;
                    this._partsUploaded += 1;
                }
                catch (_f) {
                    // `upload` throws only with its budget spent. Nothing left to try for
                    // this part; keep going, because the others may still land.
                }
            }
            if (recovered > 0) {
                this.failedParts = Math.max(0, this.failedParts - recovered);
                (_b = (_a = this.events).onDegraded) === null || _b === void 0 ? void 0 : _b.call(_a, `recovered ${recovered} held recording part(s) before finalising`);
            }
            if (recovered < missing.length) {
                (_d = (_c = this.events).onFailure) === null || _d === void 0 ? void 0 : _d.call(_c, `${missing.length - recovered} recording part(s) could not be recovered before finalising`);
            }
        });
    }
    abort(reason) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            this.uploader.abort();
            try {
                yield this.client.abort(this.uploadId);
            }
            catch (_c) {
                // A failed abort is not worth escalating: the backend sweep and the
                // AbortIncompleteMultipartUpload lifecycle rule both still reap it.
            }
            (_b = (_a = this.events).onFailure) === null || _b === void 0 ? void 0 : _b.call(_a, `upload aborted: ${reason}`);
        });
    }
}
/**
 * Assemble the whole upload chain for one interview.
 *
 * Returns a sink ready to hand to `SharedRecorder`, or `null` if the control
 * plane would not start the upload. `null` is not a crash: the caller falls
 * back to leaving the recording unsent rather than failing the interview, and
 * reports the reason. An interview must never end because a recording could
 * not start.
 */
/**
 * VA-28098 — put the previous page's held bytes into the upload it left behind.
 *
 * MUST run before `init`. `init` from a new page session makes the server
 * rotate, and a rotation COMPLETES the old upload — after which S3 refuses
 * every further part against it, these included. Worse, an old upload with no
 * parts at all is aborted outright, which is how an early reconnect used to
 * lose the entire first half of an interview: the bytes existed, they had just
 * never reached the 5 MiB floor that lets a part be uploaded.
 *
 * The held chunks are grouped back into the parts the dead page had already
 * chosen, and re-uploading is safe: S3's UploadPart is last-write-wins per
 * number, so each either fills a gap or overwrites itself with identical bytes.
 *
 * Returns what it managed to restore. Never throws and never blocks the
 * interview: a recording that cannot be recovered must still be a recording
 * that can start.
 */
/**
 * Recovery runs BEFORE the recording can start, so it is deliberately impatient.
 *
 * The uploader's normal policy — 5 attempts, 500 ms exponential — is right for a
 * part of a live recording, where the bytes are irreplaceable and there is no
 * deadline. It is wrong here: these bytes are already at risk, and every second
 * spent retrying them is a second the interview is recording nothing at all. A
 * control plane that is down would otherwise hold the start for ~7.5 s.
 */
export const RECOVERY_UPLOADER_OPTIONS = {
    maxAttemptsPerPart: 2,
    baseBackoffMs: 250,
};
/**
 * How long `finalize` will spend re-driving held parts before completing anyway.
 *
 * Wall time rather than attempts, because per-part budgets multiply and the
 * page is showing a finished interview to someone waiting to leave. Completing
 * with what S3 has is always available as the fallback, so overrunning this
 * costs a part; blocking on it costs the candidate's trust that the thing ended.
 */
export const FINALIZE_REDRIVE_BUDGET_MS = 30000;
export const recoverAbandonedChunks = (client_1, store_1, ...args_1) => __awaiter(void 0, [client_1, store_1, ...args_1], void 0, function* (client, store, events = {}, uploaderOptions = RECOVERY_UPLOADER_OPTIONS) {
    var _a, _b, _c;
    let held = null;
    try {
        held = yield store.recover();
    }
    catch (err) {
        (_a = events.onFailure) === null || _a === void 0 ? void 0 : _a.call(events, 'could not read the previous page\'s held recording', err);
        return { parts: 0, bytes: 0 };
    }
    if (!held || held.parts.length === 0)
        return { parts: 0, bytes: 0 };
    const uploader = new MultipartUploader(client, held.ref.uploadId, uploaderOptions);
    let parts = 0;
    let bytes = 0;
    try {
        // Ascending part order. A part is a byte range of one continuous stream,
        // so uploading them out of order would be assembling the recording wrong.
        for (const part of held.parts) {
            yield uploader.upload({
                partNumber: part.partNumber,
                blob: part.blob,
                size: part.size,
            });
            parts += 1;
            bytes += part.size;
        }
    }
    catch (err) {
        // `upload` only throws once it has exhausted its own retries, so there is
        // nothing left to try. Report it as degraded rather than failing the
        // interview over video that is already lost.
        (_b = events.onDegraded) === null || _b === void 0 ? void 0 : _b.call(events, `could not restore ${held.parts.length - parts} held recording part(s) from the previous page`);
        (_c = events.onFailure) === null || _c === void 0 ? void 0 : _c.call(events, 'recovering the previous page\'s recording failed', err);
    }
    finally {
        // Unconditionally, even on failure. These chunks are tagged with the OLD
        // upload's part numbers; leaving them behind would have the next
        // `remember()` re-associate them with the NEW upload, and replaying them
        // there would corrupt it. Anything not uploaded by now is not recoverable.
        try {
            yield store.clear();
        }
        catch (_d) {
            /* a store we cannot clear is a store we already cannot trust */
        }
    }
    return { parts, bytes };
});
export const createS3MultipartSink = (client_1, ...args_1) => __awaiter(void 0, [client_1, ...args_1], void 0, function* (client, events = {}, uploaderOptions = {}, store) {
    var _a, _b;
    // Before `init`, always — see `recoverAbandonedChunks`.
    // Not `uploaderOptions`: those are tuned for a live recording's parts. See
    // RECOVERY_UPLOADER_OPTIONS — recovery must not delay the start.
    const recovered = store
        ? yield recoverAbandonedChunks(client, store, events, RECOVERY_UPLOADER_OPTIONS)
        : { parts: 0, bytes: 0 };
    let init;
    try {
        init = yield client.init();
    }
    catch (err) {
        (_a = events.onFailure) === null || _a === void 0 ? void 0 : _a.call(events, 'could not initialise the recording upload', err);
        return null;
    }
    if (!(init === null || init === void 0 ? void 0 : init.uploadId) || !(init === null || init === void 0 ? void 0 : init.key)) {
        (_b = events.onFailure) === null || _b === void 0 ? void 0 : _b.call(events, 'control plane returned no uploadId');
        return null;
    }
    const uploader = new MultipartUploader(client, init.uploadId, uploaderOptions);
    // The server's part size wins when it offers one, so the floor can be raised
    // operationally without a frontend deploy.
    const bufferOptions = {};
    if (typeof init.partSize === 'number' && init.partSize > 0) {
        bufferOptions.targetPartBytes = init.partSize;
    }
    // VA-28007: continue the earlier session's numbering when resuming. Only the
    // server knows where that got to — it reads S3's own part list — so an absent
    // or nonsensical value must fall through to the default of 1 rather than be
    // guessed at here.
    if (typeof init.nextPartNumber === 'number' && init.nextPartNumber >= 1) {
        bufferOptions.startPartNumber = init.nextPartNumber;
    }
    // Only now: `remember` is what binds subsequently held chunks to an upload,
    // and binding them to this one before the recovery above had finished with
    // the previous one would mix two uploads' part numbers together.
    if (store) {
        try {
            yield store.remember({ uploadId: init.uploadId, key: init.key });
        }
        catch (_c) {
            /* durability is best-effort; the recording still works without it */
        }
    }
    const sink = new S3MultipartSink(uploader, client, init.uploadId, events, bufferOptions, store);
    return {
        sink,
        uploadId: init.uploadId,
        key: init.key,
        partSize: init.partSize,
        recovered,
    };
});
